import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import Menu, { SubMenu } from 'rc-menu';
import { connect } from 'react-redux';
import { matchRoutePath } from '../../../../../../shared/frontend/libs/router/router-helpers';
import { menuBuiltinPlacements } from '../../../../../../shared/frontend/components/menu/menuHelper';
import { RAID_DIFFICULTIES } from '../../../../../../shared/common/enums';
import { EntityAsyncSelect, TSuggestionOption } from '../../../../../../shared/frontend/components/entityAsyncSelect';
import { handleRCMenuOnClick, navigateTo } from '../../../../../../shared/frontend/libs/history';
import { findBySlug } from '../../../../../../shared/frontend/libs/utils';
import WowIcon from '../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { Expansion } from '../../../../../../shared/gameConfig/expansion';
import { Expansions, EXPANSIONS_INFO, FACTIONS, TExpansionInfo } from '../../../../shared/enums';
import { gameConfig } from '../../../../shared/gameConfig';
import { namedRoutes, resolveRoutePath } from '../../../scripts/bundles/routes';
import { TApplicationState } from '../../libs/store';
import { DropdownTitle } from '../presentational/dropdownTitle';
import {
  buildMenus,
  buildRouteParams,
  EMenuEntry,
  getRaidRegionSubMenuItemsMenuEntry,
  TMenuEntry
} from '../presentational/menuHelper';
import { raidSupportsFactions } from '../../../../../../shared/common/raids';

type TPropsFromState = any;

type TPropsThatDispatch = {};

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    ...ownProps,
    ...state.siteMeta,
    combinedRegions: [...state.siteMeta.regions, ...state.siteMeta.subRegions]
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: any): TPropsThatDispatch {
  return {};
}

function getRaidRankingsPathForParams(params: any) {
  if (params.faction) {
    if (params.realm) {
      return namedRoutes.RaidFactionRealmRankings;
    } else {
      return namedRoutes.RaidFactionRankings;
    }
  } else {
    if (params.realm) {
      return namedRoutes.RaidRealmRankings;
    } else {
      return namedRoutes.RaidRankings;
    }
  }
}

export function getBossRankingsPathForParams(params: any) {
  if (params.faction) {
    if (params.realm) {
      return namedRoutes.BossFactionRealmRankings;
    } else {
      return namedRoutes.BossFactionRankings;
    }
  } else {
    if (params.realm) {
      return namedRoutes.BossRealmRankings;
    } else {
      return namedRoutes.BossRankings;
    }
  }
}

function canShowBosses(props: any) {
  const isHallOfFame =
    props.match.path.indexOf('/hall-of-fame') !== -1 ||
    props.match.path.indexOf('/faction-hall-of-fame') !== -1 ||
    props.match.path.indexOf('/realm-hall-of-fame') !== -1; // HACK to tell if on hall of fame
  const isWorldFirstRace = props.match.path.indexOf('/race-to-world-first') !== -1; // HACK to tell if on hall of fame
  return !isHallOfFame && !isWorldFirstRace;
}

function RaidFloatingMenu(props: any) {
  if (!props.raid.slug) {
    // FIXME: we seem to get raid as a string in here sometimes
    // This should be tracked down and fixed, but for now just bailing if we aren't getting an object
    return null;
  }

  let bossTitle = (
    <span>
      {t('nav:all-bosses')} &nbsp;
      <i className="fa fa-caret-down text-raiderio" />
    </span>
  );

  if (props.match.params.boss) {
    const boss = getBoss(getRaid(props.raids, props.raid.slug).encounters, props.match.params.boss);
    if (!boss) {
      return null;
    }

    bossTitle = (
      <span>
        <WowIcon icon="" size="xx-small" overrideImageUrl={asset(boss.iconUrl)} className="slds-m-right--x-small" />
        {t(`raids:${props.raid.slug}.bosses.${boss.slug}.name`)}
        &nbsp; <i className="fa fa-caret-down text-raiderio" />
      </span>
    );
  }

  const currentRaid = props.match.params.raid ? getRaid(props.raids, props.match.params.raid) : null;

  function onRealmSuggestionSelected(__: any, option: TSuggestionOption) {
    if (option.type !== 'realm') {
      return;
    }

    const { data } = option;

    navigateTo(
      resolveRoutePath(
        props.match.params.faction ? props.routes.factionRealmRoute : props.routes.realmRoute,
        buildRouteParams(props.match.params, {
          region: data.region.slug,
          realm: data.realm.slug
        })
      )
    );
  }

  function handleSelectItemMenu(params: any) {
    handleRCMenuOnClick(params, props.hashRoute);
  }

  const menus: TMenuEntry[] = [];

  // Build out the raid float menu

  if (currentRaid) {
    const raidChildren: TMenuEntry[] = props.raids
      .filter((raid: any) => raid.expansion_id === Expansions.Current || raid.slug === currentRaid.slug)
      .map((raid: any) => ({
        type: EMenuEntry.ITEM,
        link: resolveRoutePath(
          props.match.params.boss ? getRaidRankingsPathForParams(props.match.params) : props.match.path,
          { ...props.match.params, raid: raid.slug }
        ),
        customIcon: asset(raid.icon_url),
        title: t(`raids:${raid.slug}.name`)
      }));

    menus.push({
      type: EMenuEntry.ITEM,
      title: <DropdownTitle title={t(`raids:${currentRaid.slug}.name`)} />,
      customIcon: asset(currentRaid.icon_url),
      /* Display Current Expansion Raids or the selected raid*/
      children: raidChildren
    });

    /*
     * Display Supported Expansions with submenus for their raids
     *
     * Gather all of the enums we use for expansions that are greater than or equal to the
     * first supported expansion and remove duplicates so we have a dynamic list of
     * supported expansions.
     */
    const oldExpansions: Expansion[] = _.range(Expansions.FirstSupported, Expansions.Current).reverse();
    for (const expansion of oldExpansions) {
      // We want raids from current expansion to not be in a submenu so skip them here they are handled above.

      const expansionRaids = props.raids.filter((raid: any) => raid.expansion_id === expansion);
      const expansionObj = getExpansionById(expansion);
      if (!expansionObj) {
        continue;
      }

      raidChildren.push({
        type: EMenuEntry.ITEM,
        title: t(`common:expansions.${expansionObj.slug}.name`),
        wowIcon: expansionObj.icon || 'inv_misc_token_boost_generic',
        children: expansionRaids.map(
          (raid: any): TMenuEntry => ({
            type: EMenuEntry.ITEM,
            link: resolveRoutePath(
              props.match.params.boss ? getRaidRankingsPathForParams(props.match.params) : props.match.path,
              { ...props.match.params, raid: raid.slug }
            ),
            customIcon: asset(`/images/${raid.slug}/icon.jpg`),
            title: t(`raids:${raid.slug}.name`)
          })
        )
      });
    }
  }

  const bossChildren: TMenuEntry[] = [];
  bossChildren.push({
    type: EMenuEntry.ITEM,
    link: resolveRoutePath(getRaidRankingsPathForParams(props.match.params), buildRouteParams(props.match.params)),
    title: t('nav:all-bosses'),
    className: cx(
      matchRoutePath({ path: getRaidRankingsPathForParams(props.match.params), end: false }, props.location.pathname) ||
        matchRoutePath({ path: getBossRankingsPathForParams(props.match.params), end: false }, props.location.pathname)
        ? 'rc-menu-item-selected slds-text-align--left'
        : 'slds-text-align--left'
    )
  });

  // Build out the bosses floating menu

  if (canShowBosses(props)) {
    const raid = getRaid(props.raids, props.raid.slug);

    raid.encounters.map((boss: any, index: number) => {
      bossChildren.push({
        type: EMenuEntry.ITEM,
        link: resolveRoutePath(
          getBossRankingsPathForParams(props.match.params),
          buildRouteParams(props.match.params, { boss: boss.slug })
        ),
        title: t(`raids:${raid.slug}.bosses.${boss.slug}.name`),
        customIcon: asset(boss.iconUrl)
      });
    });

    menus.push({
      type: EMenuEntry.ITEM,
      title: bossTitle,
      children: bossChildren
    });
  }

  // Build out difficulties floating menu
  const difficultiesChildren: TMenuEntry[] = [];
  menus.push({
    type: EMenuEntry.ITEM,
    title: (
      <DropdownTitle
        title={
          props.match.params.difficulty
            ? t(`common:difficulty.${getDifficulty(RAID_DIFFICULTIES, props.match.params.difficulty).slug}.name`)
            : t('common:difficulty-label')
        }
      />
    ),

    children: difficultiesChildren
  });

  getRaidDifficulties(props.match.params.raid).map((difficulty: any) => {
    difficultiesChildren.push({
      type: EMenuEntry.ITEM,
      link: resolveRoutePath(props.match.path, { ...props.match.params, difficulty: difficulty.slug }),
      title: t(`common:difficulty.${difficulty.slug}.name`)
    });
  });

  // Build out regions floating menu
  menus.push(
    getRaidRegionSubMenuItemsMenuEntry({
      ...props,
      path: props.match.params.realm ? props.routes.baseRoute : props.match.path
    })
  );

  // Build out factions floating menu

  if (raidSupportsFactions(props.raid.slug)) {
    const factionsChildren: TMenuEntry[] = [];
    menus.push({
      type: EMenuEntry.ITEM,
      title: (
        <DropdownTitle
          title={
            props.match.params.faction
              ? t(`common:factions.${props.match.params.faction}.name`)
              : t('common:all-factions')
          }
        />
      ),

      children: factionsChildren
    });

    factionsChildren.push({
      type: EMenuEntry.ITEM,
      link: resolveRoutePath(
        props.match.params.realm ? props.routes.realmRoute : props.routes.baseRoute,
        buildRouteParams(_.omit(props.match.params, 'faction'), {})
      ),
      title: t('common:all-factions')
    });

    FACTIONS.map((faction) => {
      factionsChildren.push({
        type: EMenuEntry.ITEM,
        link: resolveRoutePath(
          props.match.params.realm ? props.routes.realmFactionRoute : props.routes.factionRoute,
          buildRouteParams(props.match.params, { faction: faction.slug })
        ),
        title: t(`common:factions.${faction.slug}.name`)
      });
    });
  }

  return (
    <>
      <Menu
        onClick={handleSelectItemMenu}
        selectable={false}
        mode="horizontal"
        builtinPlacements={menuBuiltinPlacements}
        className={cx('rio-hidden--small rio-floating-menu', props.className)}
      >
        {buildMenus('raid-floating', menus)}

        <SubMenu
          title={
            <span>
              <i className="fa fa-search" />
              &nbsp; {t('rankings:find-a-realm')}
            </span>
          }
        >
          <EntityAsyncSelect
            type="realm"
            placeholder={t('rankings:enter-realm-name')}
            onChange={onRealmSuggestionSelected}
          />
        </SubMenu>
      </Menu>
    </>
  );
}

function getBoss(bosses: any, boss: any) {
  return findBySlug(bosses, boss.toLowerCase());
}

export function getExpansionById(id: number): TExpansionInfo | undefined {
  return EXPANSIONS_INFO.find((expansion: TExpansionInfo) => expansion.expansionId === id);
}

function getRaidDifficulties(raidSlug: any) {
  const raidSupportedDifficulties = gameConfig.RaidsBySlug[raidSlug].supportedDifficulties as string[];

  return RAID_DIFFICULTIES.filter((diff) => raidSupportedDifficulties.includes(diff.slug));
}

function getDifficulty(difficulties: any, difficulty: any) {
  return findBySlug(difficulties, difficulty.toLowerCase());
}

export function getRegion(regions: any, region: any) {
  return findBySlug(regions, region.toLowerCase());
}

export function getRaid(raids: any, raidSlug: any) {
  return findBySlug(raids, raidSlug.toLowerCase());
}

export default connect<TPropsFromState, TPropsThatDispatch, any>(mapStateToProps, mapDispatchToProps)(RaidFloatingMenu);
